import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Globe,
  Mail,
  Search,
  Trash2,
  User,
  UserCircle,
  Users,
  X,
} from 'lucide-react';
import { getAdminUsers, deleteAdminUser } from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import AdminPageHeader from './AdminPageHeader';
import AdminTableSkeleton from './AdminTableSkeleton';
import './AdminStyles.scss';

const typeLabels = {
  creator: 'Creador',
  business: 'Marca',
  seller: 'Vendedor',
  superuser: 'Staff / Admin',
};

const UserManagement = () => {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [filterType, setFilterType] = useState('all');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, count: 0 });
  const [revision, setRevision] = useState(0);
  const { currentUser } = useAuth();
  const canDelete = Boolean(currentUser?.is_superuser);

  useEffect(() => {
    let active = true;
    getAdminUsers({ page, type: filterType === 'all' ? '' : filterType, search: searchQuery })
      .then((response) => {
        if (!active) return;
        setUsers(response.data?.users || []);
        setPagination(response.data?.pagination || { page: 1, pages: 1, count: 0 });
      })
      .catch(() => {
        if (active) setError('No pudimos cargar el directorio de usuarios.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, filterType, searchQuery, revision]);

  useEffect(() => {
    setPage(1);
  }, [filterType, searchQuery]);

  const filteredUsers = users;

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteAdminUser(pendingDelete.id);
      setPage(1);
      setRevision(value => value + 1);
      setPendingDelete(null);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No pudimos eliminar el usuario.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const renderAvatar = (user) => {
    const avatarUrl = user.display_image_url || user.profile_picture_url;
    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt={user.username}
          className={`admin-avatar admin-avatar--${user.user_type || 'default'}`}
          style={{ objectFit: 'cover' }}
        />
      );
    }
    const initial = user.username ? user.username.charAt(0).toUpperCase() : null;
    return (
      <span className={`admin-avatar admin-avatar--${user.user_type || 'default'}`}>
        {initial || <User size={16} />}
      </span>
    );
  };

  return (
    <div className="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <AdminPageHeader
          title="Directorio de Usuarios"
          subtitle="Gestiona y supervisa los perfiles registrados en la plataforma."
          breadcrumbs="Usuarios"
        >
          <div className="admin-filter-bar">
            <label className="admin-search-field">
              <Search size={18} aria-hidden="true" />
              <span className="sr-only">Buscar usuarios</span>
              <input
                className="admin-input"
                type="search"
                placeholder="Buscar por usuario, email o ID…"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </label>
            <div className="admin-segmented-control" aria-label="Filtrar por tipo de perfil">
              {[
                { id: 'all', label: 'Todos' },
                { id: 'vip', label: 'V.I.P.' },
                { id: 'creator', label: 'Creadores' },
                { id: 'business', label: 'Marcas' },
                { id: 'seller', label: 'Vendedores' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={filterType === tab.id ? 'is-active' : ''}
                  onClick={() => setFilterType(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </AdminPageHeader>

        {/* Metric summary grid */}
        <div className="admin-metric-grid">
          <div className="admin-metric-card">
            <span>{filterType === 'all' && !searchQuery ? 'Total registrados' : 'Usuarios que coinciden'}</span>
            <strong>{pagination.count || 0}</strong>
          </div>
          <div className="admin-metric-card">
            <span>En Esta Página</span>
            <strong>{filteredUsers.length}</strong>
          </div>
          <div className="admin-metric-card">
            <span>Filtro Activo</span>
            <strong style={{ fontSize: '18px', textTransform: 'capitalize' }}>
              {filterType === 'all'
                ? 'Todos los roles'
                : filterType === 'vip'
                ? 'V.I.P.'
                : typeLabels[filterType] || filterType}
            </strong>
          </div>
          <div className="admin-metric-card">
            <span>Páginas Totales</span>
            <strong>{pagination.pages || 1}</strong>
          </div>
        </div>

        <section className="dashboard-section">
          {error && (
            <div className="alert-luxury alert-luxury--danger" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <AdminTableSkeleton columns={7} rows={8} />
          ) : (
            <>
              <div className="admin-table-container">
                <table className="admin-table">
                  <caption className="sr-only">Usuarios registrados</caption>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Perfil</th>
                      <th>Origen</th>
                      <th>Estado</th>
                      <th>Registro</th>
                      <th style={{ textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => {
                      const roleClass = user.is_superuser
                        ? 'superuser'
                        : user.user_type || 'default';
                      const roleName = user.is_superuser
                        ? 'Staff / Admin'
                        : typeLabels[user.user_type] || 'Sin rol';

                      return (
                        <tr key={user.id}>
                          <td>
                            <div className="admin-user-cell">
                              {renderAvatar(user)}
                              <div className="admin-user-info-text">
                                <strong>{user.username}</strong>
                                <div className="user-tags">
                                  {user.is_superuser && <small>Staff</small>}
                                  {user.is_vip && <small>V.I.P.</small>}
                                  {user.is_early_adopter && <small>Early</small>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="admin-muted">
                              <Mail size={14} /> {user.email}
                            </span>
                          </td>
                          <td>
                            <span className={`role-badge role-badge--${roleClass}`}>
                              {roleName}
                            </span>
                          </td>
                          <td>
                            <span className="admin-muted">
                              {user.auth_provider === 'google' ? (
                                <>
                                  <Globe size={14} /> Google
                                </>
                              ) : (
                                <>
                                  <Mail size={14} /> Email
                                </>
                              )}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`admin-status admin-status--${
                                user.is_active ? 'success' : 'danger'
                              }`}
                            >
                              {user.is_active && <CheckCircle size={14} />}{' '}
                              {user.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td>{formatDate(user.date_joined)}</td>
                          <td>
                            <div className="admin-row-actions">
                              <Link
                                to={`/profile/${user.username}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-link-luxury"
                                aria-label={`Abrir perfil de ${user.username}`}
                                title="Ver perfil"
                              >
                                <UserCircle size={18} />
                              </Link>
                              {canDelete && (
                                <button
                                  type="button"
                                  className="btn-link-luxury btn-link-luxury--danger"
                                  aria-label={`Eliminar ${user.username}`}
                                  title="Eliminar usuario"
                                  onClick={() => setPendingDelete(user)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!filteredUsers.length && (
                      <tr>
                        <td colSpan="7" className="admin-empty-state">
                          <Users
                            size={36}
                            style={{ opacity: 0.35, marginBottom: '12px', display: 'inline-block' }}
                          />
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '15px' }}>
                            No hay usuarios que coincidan con los filtros
                          </p>
                          <small style={{ color: 'inherit', opacity: 0.7, marginTop: '4px', display: 'block' }}>
                            Prueba ajustando el término de búsqueda o cambiando el filtro de perfil.
                          </small>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="admin-pagination">
                <span>
                  Mostrando <strong>{filteredUsers.length}</strong> de{' '}
                  <strong>{pagination.count || 0}</strong> usuarios registrados
                </span>
                <div className="admin-pagination__controls">
                  <button
                    type="button"
                    className="admin-pagination__btn"
                    disabled={page <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    <ChevronLeft size={16} /> Anterior
                  </button>
                  <span style={{ fontSize: '12px', fontWeight: 600, padding: '0 6px' }}>
                    Página {page} de {pagination.pages || 1}
                  </span>
                  <button
                    type="button"
                    className="admin-pagination__btn"
                    disabled={page >= (pagination.pages || 1)}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    Siguiente <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </motion.div>

      {pendingDelete && (
        <div
          className="modal-luxury-overlay"
          role="presentation"
          onMouseDown={(event) => event.target === event.currentTarget && setPendingDelete(null)}
        >
          <section
            className="modal-luxury-content admin-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-user-title"
          >
            <div className="modal-luxury-header">
              <h2 id="delete-user-title">Eliminar usuario</h2>
              <button
                type="button"
                className="close-btn-circle"
                aria-label="Cerrar"
                onClick={() => setPendingDelete(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-luxury-body">
              <p>
                Esta acción puede eliminar o anonimizar los datos de{' '}
                <strong>@{pendingDelete.username}</strong> ({pendingDelete.email}) según sus registros
                financieros y comerciales en FOTH.
              </p>
              <p style={{ marginTop: '12px', fontSize: '13px', color: '#e02424', fontWeight: 600 }}>
                ¿Deseas confirmar la eliminación?
              </p>
            </div>
            <div className="modal-luxury-footer">
              <button
                type="button"
                className="admin-btn admin-btn--outline"
                onClick={() => setPendingDelete(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                disabled={deleting}
                onClick={confirmDelete}
              >
                {deleting ? 'Eliminando…' : 'Confirmar eliminación'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
